import {
  GiftCouponForRedemption,
  PendingChargeEstimateChannel,
  PendingChargeEstimateResponse,
} from '@shared/api/giftCoupon/giftCoupon.interface';

/** couponEstimateKey가 필요로 하는 최소 형태 — lean 조회 응답도 그대로 받기 위한 구조 타입 */
export interface EstimateKeySource {
  selectedChoiceUnit?: { _id?: string } | string | null;
  giftItem?:
    | { _id?: string; giftItemInfo?: { _id?: string } | string | null }
    | string
    | null;
}

/**
 * 쿠폰의 충전예측 상품키 — 백엔드 gift-coupon.pending-charge-estimate.ts 와 동일 규칙.
 * (unit: 선택형 choiceUnit → info: 계약 원상품 → item: 폴백)
 */
export function couponEstimateKey(coupon: EstimateKeySource): string {
  if (
    coupon.selectedChoiceUnit &&
    typeof coupon.selectedChoiceUnit === 'object' &&
    coupon.selectedChoiceUnit._id
  ) {
    return `unit:${coupon.selectedChoiceUnit._id}`;
  }
  if (typeof coupon.giftItem === 'object' && coupon.giftItem) {
    const info = coupon.giftItem.giftItemInfo;
    if (info && typeof info === 'object' && info._id) {
      return `info:${info._id}`;
    }
    if (coupon.giftItem._id) {
      return `item:${coupon.giftItem._id}`;
    }
  }
  return 'item:unknown';
}

export interface SelectedChargeEstimateBucket {
  channel: PendingChargeEstimateChannel;
  count: number;
  amount: number;
}

/**
 * 선택한 쿠폰들의 매체별 충전 예상액 — 전체 예측 응답의 상품별 단가를 재적용해
 * 프론트 단독으로 계산한다 (백엔드 재호출 없음).
 * 예측 응답에 없는 키(응답 이후 새로 대기열에 들어온 상품 등)는 미분류 0원으로 잡힌다.
 */
export function computeSelectedChargeEstimate(
  estimate: PendingChargeEstimateResponse,
  selectedCoupons: GiftCouponForRedemption[],
): SelectedChargeEstimateBucket[] {
  const byKey = new Map<
    string,
    { channel: PendingChargeEstimateChannel; unitPriceWhole: number }
  >();
  for (const bucket of estimate.channels) {
    for (const item of bucket.items) {
      byKey.set(item.key, {
        channel: bucket.channel,
        unitPriceWhole: item.unitPriceWhole,
      });
    }
  }

  const buckets = new Map<string, SelectedChargeEstimateBucket>();
  for (const coupon of selectedCoupons) {
    const ref = byKey.get(couponEstimateKey(coupon));
    const channel = ref?.channel ?? null;
    const bucketKey = channel ?? 'null';
    const bucket = buckets.get(bucketKey) ?? { channel, count: 0, amount: 0 };
    bucket.count += 1;
    bucket.amount += ref?.unitPriceWhole ?? 0;
    buckets.set(bucketKey, bucket);
  }

  return Array.from(buckets.values()).sort((a, b) => {
    if (a.channel === null) return 1;
    if (b.channel === null) return -1;
    return b.amount - a.amount;
  });
}
